import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, LogOut, ArrowRight, Target, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { aboutService, authService } from '../services/api';

function AdminScopeObjectives() {
  const navigate = useNavigate();
  const [aboutId, setAboutId] = useState(null);
  const [introduction, setIntroduction] = useState('');
  const [editorialBoardDesc, setEditorialBoardDesc] = useState('');
  const [policies, setPolicies] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [specializedFields, setSpecializedFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showObjectiveForm, setShowObjectiveForm] = useState(false);
  const [editingObjectiveId, setEditingObjectiveId] = useState(null);
  const [objectiveForm, setObjectiveForm] = useState({ title: '', description: '', icon: '📌' });

  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [fieldForm, setFieldForm] = useState({ title: '', description: '', icon: '📌' });

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const response = await aboutService.get();
        if (response.data) {
          setAboutId(response.data.id);
          setIntroduction(response.data.introduction || '');
          setEditorialBoardDesc(response.data.editorialBoardDesc || '');
          if (response.data.policies) {
            try {
              const parsed = JSON.parse(response.data.policies);
              if (Array.isArray(parsed)) setPolicies(parsed);
            } catch (e) { console.error('Error parsing policies:', e); }
          }
          if (response.data.objectives) {
            try {
              const parsed = JSON.parse(response.data.objectives);
              if (Array.isArray(parsed)) setObjectives(parsed);
            } catch (e) { console.error('Error parsing objectives:', e); }
          }
          if (response.data.specializedFields) {
            try {
              const parsed = JSON.parse(response.data.specializedFields);
              if (Array.isArray(parsed)) setSpecializedFields(parsed);
            } catch (e) { console.error('Error parsing specializedFields:', e); }
          }
        }
      } catch (err) {
        setError('خطا در دریافت اطلاعات');
      } finally {
        setLoading(false);
      }
    };
    fetchAbout();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const persistAbout = async (overrides = {}) => {
    if (!aboutId) return;
    try {
      const payload = {
        introduction: overrides.introduction ?? introduction,
        editorialBoardDesc: overrides.editorialBoardDesc ?? editorialBoardDesc,
        objectives: JSON.stringify(overrides.objectives ?? objectives),
        policies: JSON.stringify(overrides.policies ?? policies),
        specializedFields: JSON.stringify(overrides.specializedFields ?? specializedFields),
      };
      await aboutService.update(aboutId, payload);
    } catch (err) {
      setError('خطا در ذخیره اطلاعات');
    }
  };

  // Objective handlers
  const handleAddObjective = async (e) => {
    e.preventDefault();
    let updated;
    if (editingObjectiveId) {
      updated = objectives.map(obj => obj.id === editingObjectiveId ? { ...obj, ...objectiveForm } : obj);
      setEditingObjectiveId(null);
    } else {
      const newItem = { id: Math.max(...objectives.map(o => o.id), 0) + 1, ...objectiveForm };
      updated = [...objectives, newItem];
    }
    setObjectives(updated);
    await persistAbout({ objectives: updated });
    setObjectiveForm({ title: '', description: '', icon: '📌' });
    setShowObjectiveForm(false);
  };

  const handleEditObjective = (obj) => {
    setObjectiveForm({ title: obj.title, description: obj.description, icon: obj.icon });
    setEditingObjectiveId(obj.id);
    setShowObjectiveForm(true);
  };

  const handleDeleteObjective = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    const updated = objectives.filter(o => o.id !== id);
    setObjectives(updated);
    await persistAbout({ objectives: updated });
  };

  const handleCancelObjective = () => {
    setShowObjectiveForm(false);
    setEditingObjectiveId(null);
    setObjectiveForm({ title: '', description: '', icon: '📌' });
  };

  // Specialized field handlers
  const handleAddField = async (e) => {
    e.preventDefault();
    let updated;
    if (editingFieldId) {
      updated = specializedFields.map(f => f.id === editingFieldId ? { ...f, ...fieldForm } : f);
      setEditingFieldId(null);
    } else {
      const newItem = { id: Math.max(...specializedFields.map(f => f.id), 0) + 1, ...fieldForm };
      updated = [...specializedFields, newItem];
    }
    setSpecializedFields(updated);
    await persistAbout({ specializedFields: updated });
    setFieldForm({ title: '', description: '', icon: '📌' });
    setShowFieldForm(false);
  };

  const handleEditField = (field) => {
    setFieldForm({ title: field.title, description: field.description, icon: field.icon });
    setEditingFieldId(field.id);
    setShowFieldForm(true);
  };

  const handleDeleteField = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    const updated = specializedFields.filter(f => f.id !== id);
    setSpecializedFields(updated);
    await persistAbout({ specializedFields: updated });
  };

  const handleCancelField = () => {
    setShowFieldForm(false);
    setEditingFieldId(null);
    setFieldForm({ title: '', description: '', icon: '📌' });
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت اهداف و دامنه</h1>
              <p className="text-xl text-blue-100">ویرایش اهداف نشریه و حوزه‌های تخصصی</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        {/* Objectives */}
        <section className="bg-white rounded-xl shadow-md p-8 mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Target className="text-blue-700" size={28} />
              اهداف نشریه
            </h2>
            {!showObjectiveForm && (
              <button onClick={() => setShowObjectiveForm(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
                <Plus size={18} />
                افزودن هدف
              </button>
            )}
          </div>

          {showObjectiveForm && (
            <form onSubmit={handleAddObjective} className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input type="text" placeholder="عنوان" value={objectiveForm.title} onChange={e => setObjectiveForm({ ...objectiveForm, title: e.target.value })} required className="px-4 py-2 border border-gray-300 rounded-lg" />
                <input type="text" placeholder="توضیحات" value={objectiveForm.description} onChange={e => setObjectiveForm({ ...objectiveForm, description: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg" />
                <input type="text" placeholder="آیکون (Emoji)" value={objectiveForm.icon} onChange={e => setObjectiveForm({ ...objectiveForm, icon: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">ذخیره</button>
                <button type="button" onClick={handleCancelObjective} className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition">انصراف</button>
              </div>
            </form>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {objectives.map(obj => (
              <div key={obj.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl">{obj.icon}</span>
                    <div>
                      <h3 className="font-bold text-gray-900">{obj.title}</h3>
                      <p className="text-gray-600 text-sm">{obj.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEditObjective(obj)} className="text-blue-600 hover:text-blue-800 text-sm">ویرایش</button>
                    <button onClick={() => handleDeleteObjective(obj.id)} className="text-red-600 hover:text-red-800 text-sm">حذف</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Specialized Fields */}
        <section className="bg-white rounded-xl shadow-md p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Globe className="text-blue-700" size={28} />
              دامنه تخصصی
            </h2>
            {!showFieldForm && (
              <button onClick={() => setShowFieldForm(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
                <Plus size={18} />
                افزودن حوزه
              </button>
            )}
          </div>

          {showFieldForm && (
            <form onSubmit={handleAddField} className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input type="text" placeholder="عنوان" value={fieldForm.title} onChange={e => setFieldForm({ ...fieldForm, title: e.target.value })} required className="px-4 py-2 border border-gray-300 rounded-lg" />
                <input type="text" placeholder="توضیحات" value={fieldForm.description} onChange={e => setFieldForm({ ...fieldForm, description: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg" />
                <input type="text" placeholder="آیکون (Emoji)" value={fieldForm.icon} onChange={e => setFieldForm({ ...fieldForm, icon: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">ذخیره</button>
                <button type="button" onClick={handleCancelField} className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition">انصراف</button>
              </div>
            </form>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specializedFields.map(field => (
              <div key={field.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl">{field.icon}</span>
                    <div>
                      <h3 className="font-bold text-gray-900">{field.title}</h3>
                      <p className="text-gray-600 text-sm">{field.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEditField(field)} className="text-blue-600 hover:text-blue-800 text-sm">ویرایش</button>
                    <button onClick={() => handleDeleteField(field.id)} className="text-red-600 hover:text-red-800 text-sm">حذف</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default AdminScopeObjectives;
